'use client';

import Link from '@/components/atoms/link';
import Markdown from '@/components/atoms/markdown';
import Breadcrumb from '@/components/molecules/breadcrumb';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import usePath from '@/hooks/usePath';
import React, { useEffect, useRef } from 'react';

const NewsAndEventsDetailsSection = ({ news }: any) => {

    const path = usePath()?.path;
    const fileteredNews = news?.find((item: any) => path?.includes(item?.url));
    const section = fileteredNews?.sections?.find(
        (sec: any) => sec?.section_with_image_text
    )?.section_with_image_text;
    const descriptionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (descriptionRef.current) {
            const links = descriptionRef.current.querySelectorAll('a');
            links.forEach((link) => {
                // Open in new tab
                link.setAttribute('target', '_blank');
                link.setAttribute('rel', 'noopener noreferrer');

                // Add button styling class to links with <u> tags
                const hasUnderline = link.querySelector('u');
                if (hasUnderline) {
                    link.classList.add('cta-button');
                }
            });
        }
    }, [section?.description]);

    if (!section) {
        return null;
    }

    const cmsCssClass = section?.additional_css || '';

    const generateBreadcrumbs = () => {
        const pathSegments = path.split('/').filter(Boolean);
        const pathSegmentsWithoutLast = pathSegments.slice(0, -1);

        return [
            ...pathSegments.map((segment, index) => {
                const href = `/${pathSegmentsWithoutLast.slice(0, index + 1).join('/')}/`;
                const isCurrentPage = pathSegments.length - 1 === index;
                return (
                    <span key={segment}>
                        {isCurrentPage ? (
                            <span
                                className="text-[17px] !font-semibold capitalize leading-6 text-black"
                                style={{ cursor: 'default' }} // Ensure no pointer cursor for static breadcrumb
                            >
                                {segment.replace(/-/g, ' ')}
                            </span>
                        ) : (
                            <Link link={href}>
                                <span className="text-[17px] font-normal capitalize leading-6 text-black">
                                    {segment.replace(/-/g, ' ')}
                                </span>
                            </Link>
                        )}
                    </span>
                );
            }),
        ];
    };

    return (
        <div>
            <div
                className={`pb-[92px] pt-3'
            relative mx-auto w-full  px-4 md:px-8 lg:px-[80px]`}
            >                    <div className="">
                    <div className="hidden pt-12 md:!block lg:px-[80px]">
                        <Breadcrumb Separator={<ChevronRightIcon className="w-4" />} className="">
                            <Link link={'/'}>
                                <span className="text-[17px] !font-[300] capitalize leading-6 text-black">
                                    Home
                                </span>
                            </Link>
                            {generateBreadcrumbs()}
                        </Breadcrumb>
                    </div>
                    <div
                        className={`flex flex-col max-w-[981px] mx-auto ${cmsCssClass}`}
                    >

                        {section?.title && (
                            <div className={`sm:my-16 my-8 ${cmsCssClass}`}>
                                <Markdown
                                    className="!text-[28px] !leading-[26px] font-semibold text-black text-center"
                                >{section.title}</Markdown>
                            </div>
                        )}

                        {section?.image?.url && (
                            <img
                                src={section.image.url}
                                alt={section.image.title || 'Case Study Image'}
                                className={`w-full h-auto max-h-[352px] object-cover ${cmsCssClass}`}
                            />
                        )}
                        {section?.description && (
                            <div ref={descriptionRef} className={`mt-[27px] ${cmsCssClass}`}>
                                <Markdown
                                    className="!text-[16px] [&>p]:!mb-[28px] !leading-[26px] !text-black !font-medium
                                    [&_table]:w-full [&_table]:mt-8 [&_table]:border-0 [&_table]:border-separate [&_table]:border-spacing-0
                                    [&_table_tbody]:border-0
                                    [&_table_tr]:border-b [&_table_tr]:border-[#E0E0E0] [&_table_tr:last-child]:border-b-0
                                    [&_table_td]:p-6 [&_table_td]:align-middle [&_table_td]:text-[16px] [&_table_td]:leading-[26px] [&_table_td]:text-[#333333] [&_table_td]:border-0
                                    [&_table_td:first-child]:w-[100px] [&_table_td:first-child]:pr-4
                                    [&_table_img]:w-[80px] [&_table_img]:h-[80px] [&_table_img]:object-contain
                                    [&_table+br]:hidden
                                    [&_table+br+p]:px-6 [&_table+br+p]:pb-6 [&_table+br+p]:pt-4 [&_table+br+p]:m-0 [&_table+br+p]:text-center
                                    [&_.cta-button]:inline-block [&_.cta-button]:px-6 [&_.cta-button]:py-3 [&_.cta-button]:bg-black [&_.cta-button]:text-[16px] [&_.cta-button]:leading-6 [&_.cta-button]:text-white [&_.cta-button]:no-underline [&_.cta-button]:font-semibold [&_.cta-button]:transition-colors [&_.cta-button]:cursor-pointer
                                    [&_.cta-button_u]:no-underline [&_.cta-button_u]:text-white
                                    [&_p:has(.cta-button)]:text-center"
                                >{section.description}</Markdown>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsAndEventsDetailsSection;
